/* eslint-disable */
import { useState, useEffect, useRef } from "react";
import { C, t, fmt$, formatDate } from "../shared/globals.js";

function PnLChart({ entries, currentPrice }) {
  const svgRef  = useRef(null);
  const wrapRef = useRef(null);
  const lineRef = useRef(null);
  const [dims, setDims]       = useState({ w: 400, h: 180 });
  const [tooltip, setTooltip] = useState(null);
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const obs = new ResizeObserver(es => {
      const { width, height } = es[0].contentRect;
      setDims({ w: Math.max(width, 100), h: Math.max(height, 80) });
    });
    if (wrapRef.current) {
      obs.observe(wrapRef.current);
      requestAnimationFrame(() => {
        if (wrapRef.current) {
          const { width } = wrapRef.current.getBoundingClientRect();
          if (width > 0) setDims(d => ({ ...d, w: width }));
        }
      });
    }
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    setAnimated(false);
    const t = setTimeout(() => setAnimated(true), 50);
    return () => clearTimeout(t);
  }, [currentPrice, entries.length]);

  useEffect(() => {
    if (!animated || !lineRef.current) return;
    const el = lineRef.current;
    const len = el.getTotalLength?.() || 2000;
    el.style.transition = "none";
    el.style.strokeDasharray = `${len}`;
    el.style.strokeDashoffset = `${len}`;
    el.style.opacity = "0";
    requestAnimationFrame(() => setTimeout(() => {
      el.style.transition = "stroke-dashoffset 1400ms cubic-bezier(0.4,0,0.2,1), opacity 200ms ease";
      el.style.strokeDashoffset = "0";
      el.style.opacity = "1";
    }, 80));
  }, [animated, dims]);

  if (!entries.length || !currentPrice) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%", color:"var(--text5)", fontFamily:"Montserrat,sans-serif", fontSize:11 }}>ENTER PRICE</div>
  );

  const sorted = [...entries].sort((a,b) => a.date.localeCompare(b.date));
  let cumShares = 0, cumDollars = 0;
  const points = sorted.map(e => {
    cumShares  += e.shares;
    cumDollars += e.dollars;
    return { date: e.date, pnl: cumShares * currentPrice - cumDollars };
  });
  const today = new Date().toISOString().split("T")[0];
  if (points[points.length-1]?.date !== today)
    points.push({ ...points[points.length-1], date: today });

  // Milestones
  const MILESTONES = [1000, 5000, 10000, 25000, 50000, 100000, 250000];

  const PAD = { t:24, r:20, b:38, l:72 };
  const W = dims.w, H = dims.h;
  const gW = W - PAD.l - PAD.r, gH = H - PAD.t - PAD.b;
  if (gW <= 0 || gH <= 0) return null;

  const pnlVals = points.map(p => p.pnl);
  const minV = Math.min(0, ...pnlVals) * 1.1;
  const maxV = Math.max(0, ...pnlVals) * 1.1 || 100;
  const rangeV = maxV - minV || 1;
  const xS = i => PAD.l + (i / Math.max(points.length-1, 1)) * gW;
  const yS = v => PAD.t + gH - ((v - minV) / rangeV) * gH;
  const zeroY = yS(0);

  let pathD = `M ${xS(0)} ${yS(points[0].pnl)}`;
  for (let i = 1; i < points.length; i++) {
    const cpx = (xS(i-1) + xS(i)) / 2;
    pathD += ` C ${cpx} ${yS(points[i-1].pnl)}, ${cpx} ${yS(points[i].pnl)}, ${xS(i)} ${yS(points[i].pnl)}`;
  }

  const lastPnl  = points[points.length-1]?.pnl || 0;
  const lineColor = lastPnl >= 0 ? C.green : C.red;
  const yTicks = Array.from({length:5}, (_,i) => minV + (rangeV/4)*i);
  const xIdxs  = [0, Math.floor((points.length-1)/2), points.length-1];

  // Hit milestones
  const hitMilestones = MILESTONES.filter(m => {
    const idx = points.findIndex(p => p.pnl >= m);
    return idx >= 0;
  }).map(m => {
    const idx = points.findIndex(p => p.pnl >= m);
    return { m, x: xS(idx), y: yS(m), date: points[idx].date };
  });

  const onMove = e => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const idx = Math.max(0, Math.min(points.length-1, Math.round(((e.clientX-rect.left-PAD.l)/gW)*(points.length-1))));
    setTooltip({ x: xS(idx), y: yS(points[idx].pnl), pt: points[idx] });
  };

  return (
    <div ref={wrapRef} style={{ position:"relative", width:"100%", height:"100%" }}>
      <svg ref={svgRef} width={W} height={H} onMouseMove={onMove} onMouseLeave={() => setTooltip(null)} style={{ display:"block", overflow:"visible" }}>
        {/* Grid */}
        {yTicks.map((v,i) => <line key={i} x1={PAD.l} y1={yS(v)} x2={W-PAD.r} y2={yS(v)} stroke="var(--border)" strokeWidth="1"/>)}
        {/* Zero line */}
        <line x1={PAD.l} y1={zeroY} x2={W-PAD.r} y2={zeroY} stroke="var(--border2)" strokeWidth="1" strokeDasharray="4 3"/>
        {yTicks.map((v,i) => (
          <text key={i} x={PAD.l-8} y={yS(v)+4} textAnchor="end" fill="var(--text5)" fontSize="9" fontFamily="Montserrat,sans-serif" fontWeight="700">
            {v >= 1000 ? `$${(v/1000).toFixed(0)}k` : v < -1000 ? `-$${(Math.abs(v)/1000).toFixed(0)}k` : `$${v.toFixed(0)}`}
          </text>
        ))}
        {xIdxs.map(idx => (
          <text key={idx} x={xS(idx)} y={H-6} textAnchor="middle" fill="var(--text5)" fontSize="9" fontFamily="Montserrat,sans-serif" fontWeight="700">
            {formatDate(points[idx].date)}
          </text>
        ))}
        {/* Line */}
        <path ref={lineRef} d={pathD} fill="none" stroke={lineColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{opacity:0}}/>
        {/* Milestone markers */}
        {hitMilestones.map(({m, x, y, date}) => (
          <g key={m}>
            <line x1={x} y1={PAD.t} x2={x} y2={PAD.t+gH} stroke="var(--border2)" strokeWidth="1" strokeDasharray="3 3"/>
            <rect x={x-1} y={y-9} width={m>=10000?36:m>=1000?28:22} height={14} rx="3" fill="#111" stroke="var(--border2)"/>
            <text x={x+1} y={y+1} textAnchor="start" fill={C.orange} fontSize="8" fontFamily="Montserrat,sans-serif" fontWeight="700">
              {m>=1000?`$${m/1000}k`:`$${m}`}
            </text>
          </g>
        ))}
        {/* Hover */}
        {tooltip && <>
          <line x1={tooltip.x} y1={PAD.t} x2={tooltip.x} y2={PAD.t+gH} stroke="var(--border2)" strokeWidth="1" strokeDasharray="3 3"/>
          <circle cx={tooltip.x} cy={tooltip.y} r="4" fill={lineColor} stroke="#000" strokeWidth="1.5"/>
        </>}
      </svg>
      {tooltip && (
        <div style={{ position:"absolute", left:Math.min(tooltip.x+12, dims.w-160), top:Math.max(tooltip.y-70,4),
          background:"var(--bg2)", border:`1px solid ${lineColor}`, borderRadius:7, padding:"9px 14px", pointerEvents:"none", fontFamily:"Montserrat,sans-serif", zIndex:10 }}>
          <div style={{ fontSize:9, color:"var(--text3)", letterSpacing:"0.2em", marginBottom:5 }}>{formatDate(tooltip.pt.date)}</div>
          <div style={{ fontSize:15, fontWeight:700, color:lineColor }}>
            {tooltip.pt.pnl >= 0 ? "+" : ""}{fmt$(tooltip.pt.pnl)}
          </div>
        </div>
      )}
    </div>
  );
}


export default PnLChart;
